<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { useModalStack } from '@gat-ship/vue-components'
  import type { StakeholderType } from '@gat-ship/types'
  import StakeholderProvider from './StakeholderProvider.vue'
  import StakeholderDetailModal from '../modals/StakeholderDetailModal.vue'
  import type { SelectStakeholderItem } from '../inputs/StakeholderSelect.vue'
  import {
    toValidStakeholderSection,
    type StakeholderDetailSection
  } from '../detail-modal/stakeholder-detail-modal-sections'
  import { useStakeholderRecentlyVisited } from '@gat-ship/stakeholder-search'

  // Open-state, the entity id, type, and active section all come from this
  // modal's entry in the central ordered stack (the `?modals=` codec owns the
  // URL). `stakeholderType` is a hint reconciled to the fetched entity below.
  const KIND = 'stakeholder'
  const stack = useModalStack()
  const entry = computed(() =>
    stack.entries.value.find((item) => item.kind === KIND)
  )

  function toValidType(value: string | null | undefined): StakeholderType {
    return value === 'vendor' || value === 'other' || value === 'client'
      ? value
      : 'client'
  }

  function updateParams(patch: Record<string, string>): void {
    const current = entry.value
    if (!current) return
    stack.update(KIND, { ...current.params, ...patch })
  }

  const open = computed({
    get: () => entry.value != null,
    set: (value) => {
      if (!value) stack.close(KIND)
    }
  })
  const stakeholderId = computed(() => entry.value?.params.id)
  const stakeholderType = computed(() =>
    toValidType(entry.value?.params.stakeholderType)
  )
  const activeSection = computed<StakeholderDetailSection>({
    get: () =>
      toValidStakeholderSection(entry.value?.params.stakeholderSection ?? null),
    set: (section) => updateParams({ stakeholderSection: section })
  })

  // fetch → state: the `t=` type is only a hint for the initial render; the
  // fetched stakeholder is authoritative. Once it loads, reconcile the stack's
  // type to the real one so a bare `?modals=stakeholder:<id>` deep link stops
  // showing the wrong (client-defaulted) detail and the URL self-corrects.
  const provider = ref<InstanceType<typeof StakeholderProvider>>()
  watch(
    () => provider.value?.datasource.stakeholder.value?.stakeholderType,
    (resolvedType) => {
      if (resolvedType && resolvedType !== stakeholderType.value) {
        updateParams({ stakeholderType: resolvedType })
      }
    }
  )

  // Record the visit whenever the modal loads or switches to a stakeholder, so
  // the overview's "Recently Visited" section updates. The retired full-page
  // detail layout recorded on the same fetched-stakeholder watch; opening the
  // detail as a modal dropped that behaviour, so re-wire it here.
  const { addToRecentlyVisitedItems } = useStakeholderRecentlyVisited()
  watch(
    () => provider.value?.datasource.stakeholder.value,
    (stakeholder) => {
      if (!stakeholder) return
      addToRecentlyVisitedItems({
        id: stakeholder.id,
        text: stakeholder.name,
        type: stakeholder.stakeholderType,
        timestamp: Date.now()
      })
    }
  )

  // Switch the modal to another stakeholder picked from the sidebar select.
  function handleSelect(item: SelectStakeholderItem) {
    updateParams({ id: item.id, stakeholderType: item.stakeholderType })
  }
</script>

<template>
  <StakeholderProvider
    ref="provider"
    :stakeholder-id="stakeholderId"
    :stakeholder-type="stakeholderType"
  >
    <StakeholderDetailModal
      v-model:open="open"
      v-model:section="activeSection"
      :stakeholder-type="stakeholderType"
      @select="handleSelect"
    />
  </StakeholderProvider>
</template>
